import type {
  Action,
  RunAfter,
  IfAction,
  ForeachAction,
  SwitchAction,
  UntilAction,
} from '@/types/workflowDefinition';
import {
  isIfAction,
  isForeachAction,
  isSwitchAction,
  isUntilAction,
} from '@/types/workflowDefinition';

/**
 * Pure (non-store) helpers for manipulating the workflow's nested action tree
 * when reparenting actions across scope branches (If/Foreach/Switch/Until).
 *
 * All functions follow the structural-sharing convention used elsewhere in
 * `workflowStore.ts`: when nothing changes, the same record is returned by
 * reference; otherwise a new record (and the spine of containers above the
 * change) is allocated.
 */

/**
 * Result of locating an action inside a specific scope branch.
 *
 * `remove()` returns a new top-level actions record with that action removed
 * from its branch and the spine above it rebuilt (other branches and siblings
 * preserve referential identity). Call exactly once per use — the closure
 * captures the actions snapshot at lookup time.
 */
export type BranchActionLocation = {
  action: Action;
  remove: () => Record<string, Action>;
};

/**
 * Apply a transformation function recursively to all nested action records
 * within a scope action (If, Foreach, Until, Switch).
 * 
 * Returns { action, changed } where action is the potentially updated action
 * and changed indicates whether any modifications occurred.
 * 
 * This helper enables structural sharing - if nothing changes, the original
 * action reference is returned.
 */
export const recurseIntoScopeAction = (
  action: Action,
  transform: (actions: Record<string, Action>) => Record<string, Action>,
): { action: Action; changed: boolean } => {
  if (isIfAction(action)) {
    const ia = action as IfAction;
    let newIa = ia;
    let ifChanged = false;
    if (ia.actions) {
      const recursed = transform(ia.actions);
      if (recursed !== ia.actions) {
        newIa = { ...newIa, actions: recursed };
        ifChanged = true;
      }
    }
    if (ia.else?.actions) {
      const recursed = transform(ia.else.actions);
      if (recursed !== ia.else.actions) {
        newIa = { ...newIa, else: { ...ia.else, actions: recursed } };
        ifChanged = true;
      }
    }
    return { action: ifChanged ? (newIa as Action) : action, changed: ifChanged };
  }

  if (isForeachAction(action)) {
    const fa = action as ForeachAction;
    if (fa.actions) {
      const recursed = transform(fa.actions);
      if (recursed !== fa.actions) {
        return { action: { ...fa, actions: recursed } as Action, changed: true };
      }
    }
    return { action, changed: false };
  }

  if (isUntilAction(action)) {
    const ua = action as UntilAction;
    if (ua.actions) {
      const recursed = transform(ua.actions);
      if (recursed !== ua.actions) {
        return { action: { ...ua, actions: recursed } as Action, changed: true };
      }
    }
    return { action, changed: false };
  }

  if (isSwitchAction(action)) {
    const sa = action as SwitchAction;
    let newSa = sa;
    let switchChanged = false;
    if (sa.cases) {
      const newCases: typeof sa.cases = {};
      for (const [cn, cd] of Object.entries(sa.cases)) {
        const tc = cd as { case: string; actions?: Record<string, Action> };
        if (tc.actions) {
          const recursed = transform(tc.actions);
          if (recursed !== tc.actions) {
            newCases[cn] = { ...tc, actions: recursed };
            switchChanged = true;
          } else {
            newCases[cn] = cd;
          }
        } else {
          newCases[cn] = cd;
        }
      }
      if (switchChanged) {
        newSa = { ...newSa, cases: newCases };
      }
    }
    if (sa.default?.actions) {
      const recursed = transform(sa.default.actions);
      if (recursed !== sa.default.actions) {
        newSa = { ...newSa, default: { ...sa.default, actions: recursed } };
        switchChanged = true;
      }
    }
    return { action: switchChanged ? (newSa as Action) : action, changed: switchChanged };
  }

  // Not a scope action
  return { action, changed: false };
};

/**
 * Determine which branch (if any) a source handle id refers to.
 *
 * Returns the branch type (`'true' | 'false' | 'loop' | 'default' | 'case-<name>'`)
 * for control-flow branch handles, or `null` for non-branch handles
 * (`'output'`, `'merge'`, `'done'`, `'input'`, etc.).
 */
export const getBranchTypeFromHandle = (
  sourceHandle: string | null | undefined,
): string | null => {
  if (!sourceHandle) return null;
  if (
    sourceHandle === 'true' ||
    sourceHandle === 'false' ||
    sourceHandle === 'loop' ||
    sourceHandle === 'default'
  ) {
    return sourceHandle;
  }
  if (sourceHandle.startsWith('case-')) {
    return sourceHandle;
  }
  return null;
};

/**
 * Remove every runAfter reference to `name` throughout the entire actions tree.
 * Returns a new actions record (structural sharing where unchanged).
 *
 * Used when an action is being moved (e.g., reparented into a branch); after
 * the move it is no longer a sibling of its former neighbours, so any
 * runAfter pointing at it would become invalid and must be stripped.
 */
export const removeRunAfterReferencesRecursive = (
  actions: Record<string, Action>,
  name: string,
): Record<string, Action> => {
  let changed = false;
  const result: Record<string, Action> = {};

  // Create a recursive transform function that captures the name parameter
  const transform = (nestedActions: Record<string, Action>): Record<string, Action> =>
    removeRunAfterReferencesRecursive(nestedActions, name);

  for (const [actionName, action] of Object.entries(actions)) {
    let updated = action;

    if (action.runAfter && Object.hasOwn(action.runAfter, name)) {
      const newRunAfter: RunAfter = { ...action.runAfter };
      delete newRunAfter[name];
      updated = { ...updated, runAfter: newRunAfter };
      changed = true;
    }

    // Recurse into scope actions using the common helper
    const recursed = recurseIntoScopeAction(updated, transform);
    if (recursed.changed) {
      updated = recursed.action;
      changed = true;
    }

    result[actionName] = updated;
  }

  return changed ? result : actions;
};

/**
 * Check whether the given action contains another action (named `name`) anywhere
 * in its nested branches. Used to detect cycles before reparenting.
 */
export const actionContainsRecursive = (action: Action, name: string): boolean => {
  if (isIfAction(action)) {
    const ia = action as IfAction;
    if (
      ia.actions &&
      (Object.hasOwn(ia.actions, name) ||
        Object.values(ia.actions).some((a) => actionContainsRecursive(a, name)))
    ) {
      return true;
    }
    if (
      ia.else?.actions &&
      (Object.hasOwn(ia.else.actions, name) ||
        Object.values(ia.else.actions).some((a) => actionContainsRecursive(a, name)))
    ) {
      return true;
    }
  }
  if (isForeachAction(action)) {
    const fa = action as ForeachAction;
    if (
      fa.actions &&
      (Object.hasOwn(fa.actions, name) ||
        Object.values(fa.actions).some((a) => actionContainsRecursive(a, name)))
    ) {
      return true;
    }
  }
  if (isUntilAction(action)) {
    const ua = action as UntilAction;
    if (
      ua.actions &&
      (Object.hasOwn(ua.actions, name) ||
        Object.values(ua.actions).some((a) => actionContainsRecursive(a, name)))
    ) {
      return true;
    }
  }
  if (isSwitchAction(action)) {
    const sa = action as SwitchAction;
    if (sa.cases) {
      for (const cd of Object.values(sa.cases)) {
        const tc = cd as { case: string; actions?: Record<string, Action> };
        if (
          tc.actions &&
          (Object.hasOwn(tc.actions, name) ||
            Object.values(tc.actions).some((a) => actionContainsRecursive(a, name)))
        ) {
          return true;
        }
      }
    }
    if (
      sa.default?.actions &&
      (Object.hasOwn(sa.default.actions, name) ||
        Object.values(sa.default.actions).some((a) => actionContainsRecursive(a, name)))
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Insert an action into a specific branch of a scope container (If/Foreach/Switch/Until)
 * located anywhere in the tree. Returns a new actions record on success, or the
 * original record if the branch was not found.
 *
 * `branchType`:
 *   - 'true' / 'false' for If
 *   - 'loop'           for Foreach / Until
 *   - 'default'        for Switch default branch
 *   - 'case-<name>'    for a specific Switch case branch
 */
export const insertActionIntoBranch = (
  actions: Record<string, Action>,
  parentActionName: string,
  branchType: string,
  insertName: string,
  insertAction: Action,
): Record<string, Action> => {
  for (const [actionName, act] of Object.entries(actions)) {
    if (actionName === parentActionName) {
      const result = { ...actions };
      if (isIfAction(act) && (branchType === 'true' || branchType === 'false')) {
        const ia = act as IfAction;
        if (branchType === 'true') {
          result[actionName] = {
            ...ia,
            actions: { ...(ia.actions ?? {}), [insertName]: insertAction },
          } as Action;
        } else {
          result[actionName] = {
            ...ia,
            else: {
              ...(ia.else ?? {}),
              actions: { ...(ia.else?.actions ?? {}), [insertName]: insertAction },
            },
          } as Action;
        }
        return result;
      }
      if (isForeachAction(act) && branchType === 'loop') {
        const fa = act as ForeachAction;
        result[actionName] = {
          ...fa,
          actions: { ...(fa.actions ?? {}), [insertName]: insertAction },
        } as Action;
        return result;
      }
      if (isUntilAction(act) && branchType === 'loop') {
        const ua = act as UntilAction;
        result[actionName] = {
          ...ua,
          actions: { ...(ua.actions ?? {}), [insertName]: insertAction },
        } as Action;
        return result;
      }
      if (isSwitchAction(act)) {
        const sa = act as SwitchAction;
        if (branchType === 'default') {
          result[actionName] = {
            ...sa,
            default: {
              ...(sa.default ?? {}),
              actions: { ...(sa.default?.actions ?? {}), [insertName]: insertAction },
            },
          } as Action;
          return result;
        }
        if (branchType.startsWith('case-') && sa.cases) {
          const caseName = branchType.replace('case-', '');
          const caseData = sa.cases[caseName] as
            | { case: string; actions?: Record<string, Action> }
            | undefined;
          if (caseData) {
            result[actionName] = {
              ...sa,
              cases: {
                ...sa.cases,
                [caseName]: {
                  ...caseData,
                  actions: { ...(caseData.actions ?? {}), [insertName]: insertAction },
                },
              },
            } as Action;
            return result;
          }
        }
      }
      // Parent matched but branchType didn't fit — return unchanged
      return actions;
    }

    // Recurse into nested scopes
    if (isIfAction(act)) {
      const ia = act as IfAction;
      if (ia.actions) {
        const recursed = insertActionIntoBranch(
          ia.actions,
          parentActionName,
          branchType,
          insertName,
          insertAction,
        );
        if (recursed !== ia.actions) {
          return { ...actions, [actionName]: { ...ia, actions: recursed } as Action };
        }
      }
      if (ia.else?.actions) {
        const recursed = insertActionIntoBranch(
          ia.else.actions,
          parentActionName,
          branchType,
          insertName,
          insertAction,
        );
        if (recursed !== ia.else.actions) {
          return {
            ...actions,
            [actionName]: { ...ia, else: { ...ia.else, actions: recursed } } as Action,
          };
        }
      }
    } else if (isForeachAction(act)) {
      const fa = act as ForeachAction;
      if (fa.actions) {
        const recursed = insertActionIntoBranch(
          fa.actions,
          parentActionName,
          branchType,
          insertName,
          insertAction,
        );
        if (recursed !== fa.actions) {
          return { ...actions, [actionName]: { ...fa, actions: recursed } as Action };
        }
      }
    } else if (isUntilAction(act)) {
      const ua = act as UntilAction;
      if (ua.actions) {
        const recursed = insertActionIntoBranch(
          ua.actions,
          parentActionName,
          branchType,
          insertName,
          insertAction,
        );
        if (recursed !== ua.actions) {
          return { ...actions, [actionName]: { ...ua, actions: recursed } as Action };
        }
      }
    } else if (isSwitchAction(act)) {
      const sa = act as SwitchAction;
      if (sa.cases) {
        for (const [cn, cd] of Object.entries(sa.cases)) {
          const tc = cd as { case: string; actions?: Record<string, Action> };
          if (tc.actions) {
            const recursed = insertActionIntoBranch(
              tc.actions,
              parentActionName,
              branchType,
              insertName,
              insertAction,
            );
            if (recursed !== tc.actions) {
              return {
                ...actions,
                [actionName]: {
                  ...sa,
                  cases: { ...sa.cases, [cn]: { ...tc, actions: recursed } },
                } as Action,
              };
            }
          }
        }
      }
      if (sa.default?.actions) {
        const recursed = insertActionIntoBranch(
          sa.default.actions,
          parentActionName,
          branchType,
          insertName,
          insertAction,
        );
        if (recursed !== sa.default.actions) {
          return {
            ...actions,
            [actionName]: {
              ...sa,
              default: { ...sa.default, actions: recursed },
            } as Action,
          };
        }
      }
    }
  }
  return actions;
};

/**
 * Find an action that lives directly inside a specific branch of a scope container.
 * Returns the action and a `remove()` callback that, when invoked, returns a new
 * top-level actions record with the action removed from that branch — or null
 * if not found.
 */
export const findActionInBranch = (
  actions: Record<string, Action>,
  parentActionName: string,
  branchType: string,
  childName: string,
): BranchActionLocation | null => {
  for (const [actionName, act] of Object.entries(actions)) {
    if (actionName === parentActionName) {
      // Located the parent. Try the requested branch.
      if (isIfAction(act) && (branchType === 'true' || branchType === 'false')) {
        const ia = act as IfAction;
        const branchActions = branchType === 'true' ? ia.actions : ia.else?.actions;
        if (branchActions && Object.hasOwn(branchActions, childName)) {
          const child = branchActions[childName]!;
          const remove = (): Record<string, Action> => {
            const newBranch = { ...branchActions };
            delete newBranch[childName];
            const newParent: Action =
              branchType === 'true'
                ? ({ ...ia, actions: newBranch } as Action)
                : ({ ...ia, else: { ...(ia.else ?? {}), actions: newBranch } } as Action);
            return { ...actions, [actionName]: newParent };
          };
          return { action: child, remove };
        }
        return null;
      }
      if (isForeachAction(act) && branchType === 'loop') {
        const fa = act as ForeachAction;
        if (fa.actions && Object.hasOwn(fa.actions, childName)) {
          const child = fa.actions[childName]!;
          const branchActions = fa.actions;
          const remove = (): Record<string, Action> => {
            const newBranch = { ...branchActions };
            delete newBranch[childName];
            return { ...actions, [actionName]: { ...fa, actions: newBranch } as Action };
          };
          return { action: child, remove };
        }
        return null;
      }
      if (isUntilAction(act) && branchType === 'loop') {
        const ua = act as UntilAction;
        if (ua.actions && Object.hasOwn(ua.actions, childName)) {
          const child = ua.actions[childName]!;
          const branchActions = ua.actions;
          const remove = (): Record<string, Action> => {
            const newBranch = { ...branchActions };
            delete newBranch[childName];
            return { ...actions, [actionName]: { ...ua, actions: newBranch } as Action };
          };
          return { action: child, remove };
        }
        return null;
      }
      if (isSwitchAction(act)) {
        const sa = act as SwitchAction;
        if (branchType === 'default') {
          const branchActions = sa.default?.actions;
          if (branchActions && Object.hasOwn(branchActions, childName)) {
            const child = branchActions[childName]!;
            const remove = (): Record<string, Action> => {
              const newBranch = { ...branchActions };
              delete newBranch[childName];
              return {
                ...actions,
                [actionName]: {
                  ...sa,
                  default: { ...(sa.default ?? {}), actions: newBranch },
                } as Action,
              };
            };
            return { action: child, remove };
          }
          return null;
        }
        if (branchType.startsWith('case-') && sa.cases) {
          const caseName = branchType.replace('case-', '');
          const caseData = sa.cases[caseName] as
            | { case: string; actions?: Record<string, Action> }
            | undefined;
          if (caseData?.actions && Object.hasOwn(caseData.actions, childName)) {
            const child = caseData.actions[childName]!;
            const branchActions = caseData.actions;
            const remove = (): Record<string, Action> => {
              const newBranch = { ...branchActions };
              delete newBranch[childName];
              return {
                ...actions,
                [actionName]: {
                  ...sa,
                  cases: {
                    ...sa.cases,
                    [caseName]: { ...caseData, actions: newBranch },
                  },
                } as Action,
              };
            };
            return { action: child, remove };
          }
          return null;
        }
      }
      // Parent matched but branch didn't fit
      return null;
    }

    // Recurse into nested scopes — wrap the inner remove() so it rebuilds the
    // outer container as well.
    const recurseInto = (
      branchActions: Record<string, Action>,
      rebuildOuter: (newBranch: Record<string, Action>) => Action,
    ): BranchActionLocation | null => {
      const found = findActionInBranch(branchActions, parentActionName, branchType, childName);
      if (!found) return null;
      const remove = (): Record<string, Action> => {
        const newInnerActions = found.remove();
        return { ...actions, [actionName]: rebuildOuter(newInnerActions) };
      };
      return { action: found.action, remove };
    };

    if (isIfAction(act)) {
      const ia = act as IfAction;
      if (ia.actions) {
        const r = recurseInto(ia.actions, (na) => ({ ...ia, actions: na }) as Action);
        if (r) return r;
      }
      if (ia.else?.actions) {
        const r = recurseInto(
          ia.else.actions,
          (na) => ({ ...ia, else: { ...ia.else, actions: na } }) as Action,
        );
        if (r) return r;
      }
    } else if (isForeachAction(act)) {
      const fa = act as ForeachAction;
      if (fa.actions) {
        const r = recurseInto(fa.actions, (na) => ({ ...fa, actions: na }) as Action);
        if (r) return r;
      }
    } else if (isUntilAction(act)) {
      const ua = act as UntilAction;
      if (ua.actions) {
        const r = recurseInto(ua.actions, (na) => ({ ...ua, actions: na }) as Action);
        if (r) return r;
      }
    } else if (isSwitchAction(act)) {
      const sa = act as SwitchAction;
      if (sa.cases) {
        for (const [cn, cd] of Object.entries(sa.cases)) {
          const tc = cd as { case: string; actions?: Record<string, Action> };
          if (tc.actions) {
            const r = recurseInto(
              tc.actions,
              (na) =>
                ({
                  ...sa,
                  cases: { ...sa.cases, [cn]: { ...tc, actions: na } },
                }) as Action,
            );
            if (r) return r;
          }
        }
      }
      if (sa.default?.actions) {
        const r = recurseInto(
          sa.default.actions,
          (na) => ({ ...sa, default: { ...sa.default, actions: na } }) as Action,
        );
        if (r) return r;
      }
    }
  }
  return null;
};

/**
 * Reform runAfter dependencies after deleting an action throughout the entire actions tree.
 * 
 * This function:
 * 1. Removes the deleted action from all runAfter entries that reference it
 * 2. Adds the deleted action's predecessors to those successors, so they don't lose their edges
 * 
 * Returns a new actions record (structural sharing where unchanged).
 */
export const reformRunAfterOnDeleteRecursive = (
  actions: Record<string, Action>,
  deletedActionName: string,
  deletedActionRunAfter: RunAfter,
): Record<string, Action> => {
  let changed = false;
  const result: Record<string, Action> = {};
  const predecessors = Object.keys(deletedActionRunAfter);

  // Create a recursive transform function that captures the deletion parameters
  const transform = (nestedActions: Record<string, Action>): Record<string, Action> =>
    reformRunAfterOnDeleteRecursive(nestedActions, deletedActionName, deletedActionRunAfter);

  for (const [actionName, action] of Object.entries(actions)) {
    let updated = action;

    // Check if this action has a runAfter dependency on the deleted action
    if (action.runAfter && Object.hasOwn(action.runAfter, deletedActionName)) {
      // Remove the deleted action from runAfter
      const newRunAfter: RunAfter = { ...action.runAfter };
      delete newRunAfter[deletedActionName];

      // Add the deleted action's predecessors as new dependencies
      // Use the original run statuses from deletedActionRunAfter
      for (const predecessor of predecessors) {
        const predecessorStatuses = deletedActionRunAfter[predecessor] ?? ['Succeeded'];
        if (newRunAfter[predecessor]) {
          // Predecessor already exists - merge the statuses (add any missing ones)
          const existingStatuses = new Set(newRunAfter[predecessor]);
          for (const status of predecessorStatuses) {
            existingStatuses.add(status);
          }
          newRunAfter[predecessor] = Array.from(existingStatuses);
        } else {
          // Predecessor doesn't exist - add it with the original statuses
          newRunAfter[predecessor] = [...predecessorStatuses];
        }
      }

      updated = {
        ...updated,
        runAfter: Object.keys(newRunAfter).length > 0 ? newRunAfter : undefined,
      };
      changed = true;
    }

    // Recurse into scope actions using the common helper
    const recursed = recurseIntoScopeAction(updated, transform);
    if (recursed.changed) {
      updated = recursed.action;
      changed = true;
    }

    result[actionName] = updated;
  }

  return changed ? result : actions;
};
